'use client';

import { Radio, RadioGroup } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { ArrowDownRightIcon, ArrowUpRightIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { ReactNode, useEffect, useState } from 'react';
import ModalForm from '../Modal/Modal';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import CustomRichText from '../CustomRichText';
import { useCurrentLocale } from '@/utils/hooks/useCurrentLocale';
import { localeText } from '@/utils/constantTxt/localeText';

const defaultTiers = [
  {
    name: 'базовий',
    id: 'tier-base',
    href: '#',
    price: {
      monthly: '1 400',
      quart: '1 330',
      halfyear: '1 260',
      annually: '1 120',
    },
    description: 'The essentials to provide your best work for clients.',
    features: [
      {
        name: 'Свідоцтво про базову середню освіту державного зразка',
        enters: true,
      },
      {
        name: 'Доступ до навчальної платформи 24/7',
        enters: true,
      },
      {
        name: 'Авторські навчальні матеріали',
        enters: true,
      },
      {
        name: 'Індивідуальний звіт про успішність дитини',
        enters: true,
      },
      {
        name: 'Загальна підтримка від куратора класу',
        enters: true,
      },
      {
        name: 'Понад 20 авторських уроків',
        enters: true,
      },
      {
        name: 'Понад 7 тис. факультативних уроків (ІТ, маркетинг, лідерство, бізнес-менеджемент тощо)',
        enters: true,
      },
      {
        name: 'Записи уроків у форматі Close to You',
        enters: true,
      },
      {
        name: 'Забезпечення технікою для якісного навчання (навчальний ноутбук-трансформер)',
        enters: true,
      },
      {
        name: 'Навчання у форматі гри із розвитком свого героя',
        enters: false,
      },
      {
        name: 'Уроки в живому форматі',
        enters: false,
      },
      {
        name: 'Індивідуальний графік уроків відповідно до побажань батьків та дитини',
        enters: false,
      },
      {
        name: 'Письмова гарантія, що дитина буде вільно володіти 1 іноземною мовою',
        enters: false,
      },
      {
        name: 'Мовні практики з викладачем у групі до 6 дітей 3 рази/тиждень',
        enters: false,
      },
      {
        name: 'Клуби за інтересами',
        enters: false,
      },
      {
        name: 'Змагання з іншими учнями у місячному рейтингу із призами',
        enters: false,
      },
      {
        name: 'Підбір ВНЗ (в Україні і за кордоном), підготовка до вступу, повний супровід у вступній кампанії',
        enters: false,
      },
      {
        name: 'Зустрічі із топ-спеціалістами різних професій за інтересами учня',
        enters: false,
      },
      {
        name: 'Письмова гарантія вступу в університет без жодних репетиторів',
        enters: false,
      },
    ],
    superPrice: false,
  },
  {
    name: 'стандарт',
    id: 'tier-standart',
    href: '#',
    price: {
      monthly: '3 900',
      quart: '3 705',
      halfyear: '3 510',
      annually: '3 120',
    },
    description: 'A plan that scales with your rapidly growing business.',
    features: [
      {
        name: 'Свідоцтво про базову середню освіту державного зразка',
        enters: true,
      },
      {
        name: 'Доступ до навчальної платформи 24/7',
        enters: true,
      },
      {
        name: 'Авторські навчальні матеріали',
        enters: true,
      },
      {
        name: 'Індивідуальний звіт про успішність дитини',
        enters: true,
      },
      {
        name: 'Загальна підтримка від куратора класу',
        enters: true,
      },
      {
        name: 'Понад 20 авторських уроків',
        enters: true,
      },
      {
        name: 'Понад 7 тис. факультативних уроків (ІТ, маркетинг, лідерство, бізнес-менеджемент тощо)',
        enters: true,
      },
      {
        name: 'Записи уроків у форматі Close to You',
        enters: true,
      },
      {
        name: 'Забезпечення технікою для якісного навчання (навчальний ноутбук-трансформер)',
        enters: true,
      },
      {
        name: 'Навчання у форматі гри із розвитком свого героя',
        enters: true,
      },
      {
        name: 'Уроки в живому форматі',
        enters: true,
      },
      {
        name: 'Індивідуальний графік уроків відповідно до побажань батьків та дитини',
        enters: true,
      },
      {
        name: 'Письмова гарантія, що дитина буде вільно володіти 1 іноземною мовою',
        enters: true,
      },
      {
        name: 'Мовні практики з викладачем у групі до 6 дітей 3 рази/тиждень',
        enters: true,
      },
      {
        name: 'Клуби за інтересами',
        enters: true,
      },
      {
        name: 'Змагання з іншими учнями у місячному рейтингу із призами',
        enters: true,
      },
      {
        name: 'Підбір ВНЗ (в Україні і за кордоном), підготовка до вступу, повний супровід у вступній кампанії',
        enters: false,
      },
      {
        name: 'Зустрічі із топ-спеціалістами різних професій за інтересами учня',
        enters: false,
      },
      {
        name: 'Письмова гарантія вступу в університет без жодних репетиторів',
        enters: false,
      },
    ],
    superPrice: false,
  },
  {
    name: 'професійний',
    id: 'tier-proff',
    href: '#',
    price: {
      monthly: '5 900',
      quart: '5 605',
      halfyear: '5 310',
      annually: '4 720',
    },
    description: 'Dedicated support and infrastructure for your company.',
    features: [
      {
        name: 'Свідоцтво про базову середню освіту державного зразка',
        enters: true,
      },
      {
        name: 'Доступ до навчальної платформи 24/7',
        enters: true,
      },
      {
        name: 'Авторські навчальні матеріали',
        enters: true,
      },
      {
        name: 'Індивідуальний звіт про успішність дитини',
        enters: true,
      },
      {
        name: 'Загальна підтримка від куратора класу',
        enters: true,
      },
      {
        name: 'Понад 20 авторських уроків',
        enters: true,
      },
      {
        name: 'Понад 7 тис. факультативних уроків (ІТ, маркетинг, лідерство, бізнес-менеджемент тощо)',
        enters: true,
      },
      {
        name: 'Записи уроків у форматі Close to You',
        enters: true,
      },
      {
        name: 'Забезпечення технікою для якісного навчання (навчальний ноутбук-трансформер)',
        enters: true,
      },
      {
        name: 'Навчання у форматі гри із розвитком свого героя',
        enters: true,
      },
      {
        name: 'Уроки в живому форматі',
        enters: true,
      },
      {
        name: 'Індивідуальний графік уроків відповідно до побажань батьків та дитини',
        enters: true,
      },
      {
        name: 'Письмова гарантія, що дитина буде вільно володіти 1 іноземною мовою',
        enters: true,
      },
      {
        name: 'Мовні практики з викладачем у групі до 6 дітей 3 рази/тиждень',
        enters: true,
      },
      {
        name: 'Клуби за інтересами',
        enters: true,
      },
      {
        name: 'Змагання з іншими учнями у місячному рейтингу із призами',
        enters: true,
      },
      {
        name: 'Підбір ВНЗ (в Україні і за кордоном), підготовка до вступу, повний супровід у вступній кампанії',
        enters: true,
      },
      {
        name: 'Зустрічі із топ-спеціалістами різних професій за інтересами учня',
        enters: true,
      },
      {
        name: 'Письмова гарантія вступу в університет без жодних репетиторів',
        enters: true,
      },
    ],
    superPrice: false,
  },
];

type FrequencyType = 'monthly' | 'quart' | 'halfyear' | 'annually' | string;
type PriceType = {
  monthly: string;
  quart: string;
  halfyear: string;
  annually: string;
  [key: string]: string; // Allow any string key for custom frequencies
};

type FeatureType = {
  name: ReactNode | SerializedEditorState;
  enters: boolean;
};

type PlanType = {
  name: string;
  id: string;
  href: string;
  price: PriceType;
  description: ReactNode | SerializedEditorState;
  features: FeatureType[];
  superPrice: boolean;
};

export interface PriceBlockSchoolProps {
  id?: string;
  title: ReactNode | SerializedEditorState;
  subtitle: ReactNode | SerializedEditorState;
  selectPackageLabel: string;
  viewPackageLabel: string;
  hideFeaturesLabel: string;
  showFeaturesLabel: string;
  modalTagPrefix: string;
  useDefaultTires?: boolean;
  tiers?: PlanType[];
}

export default function PriceBlockSchool({
  id,
  title,
  subtitle,
  selectPackageLabel,
  viewPackageLabel,
  hideFeaturesLabel,
  showFeaturesLabel,
  modalTagPrefix,
  useDefaultTires = true,
  tiers,
}: PriceBlockSchoolProps) {
  const loc = useCurrentLocale();

  const frequencies = [
    {
      value: 'monthly' as FrequencyType,
      label: localeText[loc].month,
      priceSuffix: localeText[loc].uahMonth,
    },
    {
      value: 'quart' as FrequencyType,
      label: localeText[loc].threeMonth,
      priceSuffix: localeText[loc].uahMonth,
      discount: '-5%',
    },
    {
      value: 'halfyear' as FrequencyType,
      label: localeText[loc].halfYear,
      priceSuffix: localeText[loc].uahMonth,
      discount: '-10%',
    },
    {
      value: 'annually' as FrequencyType,
      label: localeText[loc].year,
      priceSuffix: localeText[loc].uahMonth,
      discount: '-20%',
    },
  ];

  const [dispayedTiers] = useState(useDefaultTires || !tiers?.length ? defaultTiers : tiers);
  const [frequency, setFrequency] = useState(frequencies[0]);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<PlanType | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<PlanType | null>(null);

  const openForm = () => setIsFormOpen(true);
  const closeForm = () => setIsFormOpen(false);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window?.innerWidth < 1024);
    window?.addEventListener('resize', handleResize);
    return () => window?.removeEventListener('resize', handleResize);
  }, []);

  const selectPackage = (tier: PlanType) => {
    setSelectedPackage(tier);
    openForm();
  };

  const toggleFeatures = () => {
    setShowAllFeatures(!showAllFeatures);
  };

  useEffect(() => {
    const hashId = window?.document.location.hash.slice(1);
    if (id === hashId) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [id]);

  return (
    <>
      <div id={id} data-meta="PriceBlockSchool" className="py-14">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <CustomRichText
              data={title}
              tag="div"
              className="mt-2 text-4xl font-bold tracking-tight text-white sm:text-5xl"
            />
          </div>
          <CustomRichText
            data={subtitle}
            tag="div"
            className="mx-auto mt-6 max-w-7xl text-center leading-8 text-white"
          />
          <div className="mt-8 lg:flex justify-center max-lg:w-full lg:min-w-max">
            <fieldset aria-label="Payment frequency">
              {isSmallScreen ? (
                <div className="max-w-md mx-auto">
                  <div className="grid grid-cols-2 gap-2">
                    {frequencies.map(option => (
                      <button
                        key={option.value}
                        onClick={() => setFrequency(option)}
                        className={`flex justify-between items-center px-6 py-3 rounded-full shadow-md ${
                          frequency.value === option.value
                            ? 'bg-custom-btn-gradient-violet text-white'
                            : 'bg-white text-black'
                        }`}
                      >
                        <span className="text-sm uppercase font-semibold truncate">
                          {option.label}
                        </span>
                        {option.discount ? (
                          <span className="rounded-full px-2 py-1 text-center text-sm">
                            {option.discount}
                          </span>
                        ) : (
                          <ArrowDownRightIcon className="h-6 w-6" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <RadioGroup
                  value={frequency}
                  onChange={setFrequency}
                  className="hidden bg-white lg:grid lg:items-center lg:grid-cols-4 gap-x-8 rounded-3xl lg:rounded-full p-1 font-semibold leading-5 ring-1 ring-inset ring-gray-200"
                >
                  {frequencies.map(option => (
                    <Radio
                      key={option.value}
                      value={option}
                      className={`cursor-pointer rounded-full py-2 text-md ${frequency.value === option.value && 'bg-custom-btn-gradient-violet'} lg:text-gray-600`}
                    >
                      <div
                        className={`${
                          frequency.value === option.value
                            ? 'px-8 rounded-full text-white flex justify-between items-center h-7 w-7 m-[5px]'
                            : 'text-center'
                        } lg:min-w-[220px] flex-1`}
                      >
                        <span className="uppercase">{option.label}</span>
                        {frequency.value === option.value &&
                          (option.discount ? (
                            <span className="uppercase text-center text-white">
                              {option.discount}
                            </span>
                          ) : (
                            <ArrowDownRightIcon className="h-7 w-7 m-[5px]" />
                          ))}
                      </div>
                    </Radio>
                  ))}
                </RadioGroup>
              )}
            </fieldset>
          </div>
          <div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
            {dispayedTiers.map(tier => (
              <div
                key={tier.id}
                className={`${
                  tier.superPrice ? 'ring-2 ring-indigo-600' : 'ring-1 ring-gray-200'
                } rounded-3xl p-4 lg:p-4 justify-between bg-white`}
              >
                <div className="flex lg:flex-col max-lg:gap-2 max-lg:justify-between border-b-2 pb-4 border-violet-200">
                  <div className="flex items-center justify-between gap-x-4">
                    <p
                      id={tier.id}
                      className="uppercase text-white rounded-full py-1 px-4 bg-custom-btn-gradient-violet text-sm font-semibold lg:leading-6"
                    >
                      {tier.name}
                    </p>
                  </div>
                  <p className="lg:mt-6 flex items-baseline gap-x-1">
                    <span className="text-xl lg:text-4xl font-bold tracking-tight text-gray-900">
                      {tier.price[frequency.value as keyof typeof tier.price]}
                    </span>
                    <span className="text-sm font-semibold leading-6 text-gray-900">
                      {frequency.priceSuffix}
                    </span>
                  </p>
                </div>
                <button
                  aria-describedby={tier.id}
                  onClick={() => selectPackage(tier)}
                  className={`${
                    tier.superPrice ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-900'
                  } hidden uppercase mt-6 lg:flex items-center justify-center w-full ring-1 ring-inset ring-violet-500 rounded-full px-1 py-2 text-sm font-bold leading-6 relative`}
                >
                  <span>{selectPackageLabel}</span>
                  <div className="absolute right-1 flex items-center">
                    <div className="rounded-full custom-gradient-violet-btn">
                      <ArrowUpRightIcon className="text-white h-6 w-6 m-1" />
                    </div>
                  </div>
                </button>
                <ul className="hidden lg:block mt-8 space-y-3 text-sm leading-6 text-gray-600 xl:mt-10 transition-all">
                  {(tier.features.length > 5
                    ? tier.features
                        .slice(0, showAllFeatures ? tier.features.length : 3)
                        .concat(
                          !showAllFeatures ? tier.features.slice(tier.features.length - 2) : [],
                        )
                    : tier.features
                  ).map((feature, index) => (
                    <li key={index} className="flex gap-x-3">
                      <CheckIcon
                        aria-hidden="true"
                        className={`${
                          feature.enters
                            ? 'text-white custom-gradient-violet-btn'
                            : 'bg-violet-600/40'
                        } h-6 w-6 flex-none text-white rounded-full`}
                      />
                      <CustomRichText
                        data={feature.name}
                        tag="span"
                        className={`${feature.enters ? 'text-gray-900' : 'text-gray-300'}`}
                      />
                    </li>
                  ))}
                </ul>
                <button
                  onClick={toggleFeatures}
                  className="hidden lg:block p-6 mt-4 text-violet-600 font-semibold"
                >
                  {showAllFeatures ? hideFeaturesLabel : showFeaturesLabel}
                </button>
                <button
                  aria-describedby={tier.id}
                  onClick={() => setSelectedTier(tier)}
                  className={`${
                    tier.superPrice ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-900'
                  } hidden uppercase mt-6 max-lg:flex items-center justify-center w-full ring-1 ring-inset ring-violet-500 rounded-full px-1 py-2 text-sm font-semibold leading-6 relative`}
                >
                  <span>{viewPackageLabel}</span>
                  <div className="absolute right-1 flex items-center">
                    <div className="rounded-full custom-gradient-violet-btn">
                      <ArrowUpRightIcon className="text-white h-6 w-6 m-1" />
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      {selectedTier && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-start justify-start overflow-y-auto w-full h-lvh">
          <div className="bg-white p-2 rounded-xl">
            <div className="w-full flex justify-end p-2">
              <button onClick={() => setSelectedTier(null)} aria-label="Закрити">
                <XCircleIcon className="h-6 w-6 text-violet-600" />
              </button>
            </div>
            <div className="p-4 lg:p-8 xl:p-10 justify-between bg-white">
              <div className="flex lg:flex-col max-lg:gap-2 max-lg:justify-between">
                <div className="flex items-center justify-between gap-x-4">
                  <p
                    id={selectedTier.id}
                    className="uppercase text-white rounded-full py-1 px-2 bg-custom-btn-gradient-violet text-base font-semibold lg:leading-8"
                  >
                    {selectedTier.name}
                  </p>
                </div>
                <p className="lg:mt-6 flex items-baseline gap-x-1">
                  <span className="text-xl lg:text-4xl font-bold tracking-tight text-gray-900">
                    {selectedTier.price[frequency.value as keyof typeof selectedTier.price]}
                  </span>
                  <span className="text-sm font-semibold leading-6 text-gray-600">
                    {frequency.priceSuffix}
                  </span>
                </p>
              </div>
              <button
                aria-describedby={selectedTier.id}
                onClick={() => selectPackage(selectedTier)}
                className={`${
                  selectedTier.superPrice ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-900'
                } uppercase mt-6 lg:flex items-center justify-center w-full ring-1 ring-inset ring-violet-500 rounded-full px-1 py-2 text-sm font-semibold leading-6 relative`}
              >
                <span>{selectPackageLabel}</span>
                <div className="absolute right-1 flex items-center">
                  <div className="rounded-full custom-gradient-violet-btn">
                    <ArrowUpRightIcon className="text-white h-6 w-6 m-1" />
                  </div>
                </div>
              </button>
              <ul className="mt-8 space-y-3 text-sm leading-6 text-gray-600 xl:mt-10 transition-all">
                {selectedTier.features.map((feature, index) => (
                  <li key={index} className="flex gap-x-3">
                    <CheckIcon
                      aria-hidden="true"
                      className={`${
                        feature.enters
                          ? 'text-white custom-gradient-violet-btn'
                          : 'bg-violet-600/40'
                      } h-6 w-6 flex-none text-white rounded-full`}
                    />
                    <CustomRichText
                      data={feature.name}
                      tag="span"
                      className={`${feature.enters ? 'text-gray-900' : 'text-gray-300'}`}
                    />
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setSelectedTier(null)}
                aria-describedby={selectedTier.id}
                className={`${
                  selectedTier.superPrice ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-900'
                } uppercase mt-6 lg:flex items-center justify-center w-full ring-1 ring-inset ring-violet-500 rounded-full px-1 py-2 text-sm font-semibold leading-6 relative`}
              >
                <span>{hideFeaturesLabel}</span>
                <div className="absolute isolate top-1 right-1 flex items-center">
                  <div className="rounded-full custom-gradient-violet-btn">
                    <ArrowUpRightIcon className="text-white h-6 w-6 m-1" />
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
      {isFormOpen && selectedPackage && (
        <ModalForm
          closeForm={closeForm}
          btnMeta={{
            tag: `${modalTagPrefix}-${selectedPackage?.name}-${frequency.label}`,
          }}
          color={'violet'}
        />
      )}
    </>
  );
}
